// PaymentDueView.js — 발주 계획 앱의 '지급기일 관제'를 재고 앱에서 읽기 전용으로 보는 화면.
//
// 탭 3개: 통합 대시보드(기본) / 주간 자금소요 / 월간 자금소요.
// 숫자는 발주 앱과 동일한 산정 로직(paymentDue.js)을 쓰므로 두 앱의 값이 일치해야 한다.
// 재고 앱은 조회 전용이라 '지급 완료' 기록 같은 쓰기 동작은 넣지 않는다 — 지급 처리는 발주 앱에서 한다.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, X, RefreshCw, ExternalLink } from "lucide-react";
import { fetchProcurementPlans } from "./procurementPlans";
import {
  dueRows, dueOpen, dueSumK, dueWeeks, dueMonths, dueKpis, groupBy,
  dueMD, DUE_ST, won, getFxMode, setFxMode,
} from "./paymentDue";

const PROCUREMENT_URL = "https://project-procurement-plan.web.app/";

const PILL = {
  urgent: "bg-rose-50 text-rose-700 border-rose-200",
  warn: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const Pill = ({ tone, children }) => (
  <span className={"inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold border whitespace-nowrap " + (PILL[tone] || PILL.info)}>{children}</span>
);
const Card = ({ label, value, unit, sub, danger }) => (
  <div className={"rounded-xl p-3.5 border " + (danger ? "bg-rose-900 border-rose-900" : "bg-slate-800 border-slate-800")}>
    <div className="text-[11px] text-slate-300 mb-1">{label}</div>
    <div className="text-white font-bold text-[19px] leading-tight tabular-nums">
      {value}
      {unit && <span className="text-[11px] font-medium text-slate-300 ml-0.5">{unit}</span>}
    </div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);
const Bars = ({ items, empty }) => {
  const max = items.reduce((m, x) => Math.max(m, x.v), 0);
  const tot = items.reduce((t, x) => t + x.v, 0);
  if (!items.length) return <p className="text-xs text-slate-400 py-2">{empty || "표시할 건이 없습니다."}</p>;
  return (
    <div className="space-y-2">
      {items.map((x) => (
        <div key={x.n}>
          <div className="flex justify-between text-[11.5px] mb-1">
            <span className="text-slate-600 truncate pr-2">{x.n}</span>
            <span className="text-slate-500 tabular-nums whitespace-nowrap">
              {Math.round(x.v / 1e6).toLocaleString("ko-KR")}백만 · {tot > 0 ? ((x.v / tot) * 100).toFixed(1) : 0}%
            </span>
          </div>
          <div className="h-1.5 rounded bg-slate-200">
            <div className="h-full rounded bg-teal-700" style={{ width: (max > 0 ? (x.v / max) * 100 : 0) + "%" }} />
          </div>
        </div>
      ))}
    </div>
  );
};
// 분할선적 참조(B/L·인보이스) — 발주 앱에서 입력한 값을 읽기 전용으로 보여 준다.
const ShipRef = ({ r }) => {
  if (!r.blNo && !r.invNo && !r.estAmt) return null;
  return (
    <>
      {(r.blNo || r.invNo) && (
        <div className="font-mono text-[10.5px] text-slate-400 whitespace-nowrap mt-0.5">
          {r.blNo && <>B/L <b className="text-slate-700 font-semibold">{r.blNo}</b></>}
          {r.blNo && r.invNo && " · "}
          {r.invNo && <>INV <b className="text-slate-700 font-semibold">{r.invNo}</b></>}
        </div>
      )}
      {r.estAmt && <div className="text-[10.5px] font-semibold text-amber-600 mt-0.5">금액 미입력 · 균등배분</div>}
    </>
  );
};
const money = (r) =>
  r.cur === "KRW" ? won(r.amount) + "원" : r.cur + " " + r.amount.toLocaleString("en-US", { maximumFractionDigits: 0 });

// 한 기간(주차·월)의 건별 내역 — 좌측 세로선으로 상위 행과 묶는다
const ItemBlock = ({ bucket, cap }) => {
  if (!bucket.items.length) return null;
  const hasOv = bucket.items.some((r) => r.status === "OVERDUE");
  return (
    <div className={"ml-5 pl-3.5 py-2 border-l-[3px] " + (hasOv ? "border-rose-500" : "border-teal-700")}>
      <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">
        {cap} · {bucket.items.length}건 · ₩{won(bucket.total)}
      </div>
      <div className="space-y-0.5">
        {bucket.items.map((r, i) => (
          <div
            key={r.planId + "-" + r.seq}
            className={"grid gap-3 items-baseline text-[11.5px] px-2 py-1 rounded border " +
              (r.status === "OVERDUE" ? "bg-rose-50 border-rose-200 text-rose-800" : i % 2 ? "bg-white border-slate-200" : "border-transparent")}
            style={{ gridTemplateColumns: "46px 104px minmax(120px,1.4fr) minmax(88px,.9fr) 74px minmax(100px,1fr) minmax(112px,1fr)" }}
          >
            <span className="tabular-nums text-slate-400">{dueMD(r.due)}</span>
            <span className="font-mono text-[11px] font-bold text-rose-700">{r.slipNo || "—"}</span>
            <span className="font-semibold text-slate-800 truncate" title={r.vendor}>{r.vendor}</span>
            <span className="text-slate-500" title={[r.blNo && "B/L " + r.blNo, r.invNo && "INV " + r.invNo].filter(Boolean).join(" · ")}>
              {r.label}{!r.confirmed && <span className="ml-1 text-[9.5px] text-slate-400">추정</span>}
              {r.invNo && <span className="block font-mono text-[10px] text-slate-400 truncate">INV {r.invNo}</span>}
            </span>
            <span><Pill tone={DUE_ST[r.status][1]}>{DUE_ST[r.status][0]}</Pill></span>
            <span className="text-right tabular-nums text-slate-500">{money(r)}</span>
            <span className="text-right tabular-nums font-semibold text-slate-800">
              {r.krw == null ? <span className="text-amber-600">환율 미입력</span> : "₩" + won(r.krw)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// liveFx / fxAt — 재고 앱 첫 화면 상단 '오늘의 환율' 티커의 USD/KRW 와 기준일.
// 환율은 이 화면에서 따로 입력하지 않고 첫 화면 값을 그대로 쓴다.
export default function PaymentDueView({ onClose, liveFx = 0, fxAt = "" }) {
  const [plans, setPlans] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dash");
  const [fxMode, setFxModeState] = useState(getFxMode());
  const [fStatus, setFStatus] = useState("");
  const [fVendor, setFVendor] = useState("");
  const fx = Number(liveFx) > 0 ? Number(liveFx) : 0;

  const load = useCallback(async () => {
    setLoading(true);
    try { setPlans(await fetchProcurementPlans()); setErr(""); }
    catch (e) { setErr(e.message || String(e)); setPlans((p) => p || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 환율은 첫 화면 '오늘의 환율'을 그대로 주입 — 값이 갱신되면 전체 재계산된다.
  const rows = useMemo(() => (plans ? dueRows(plans, { fx, fxMode }) : []), [plans, fx, fxMode]);
  const k = useMemo(() => dueKpis(rows), [rows]);
  const weeks = useMemo(() => dueWeeks(rows), [rows]);
  const months = useMemo(() => dueMonths(rows), [rows]);
  const open = k.open;

  const applyFxMode = (m) => { setFxMode(m); setFxModeState(m); };

  const vendors = useMemo(() => Array.from(new Set(rows.map((r) => r.vendor))).sort(), [rows]);
  const ledger = useMemo(() => {
    let l = dueOpen(rows);
    if (fStatus) l = l.filter((r) => r.status === fStatus);
    if (fVendor) l = l.filter((r) => r.vendor === fVendor);
    return l;
  }, [rows, fStatus, fVendor]);

  const curs = useMemo(() => Array.from(new Set(open.map((r) => r.cur))).sort(), [open]);
  const cm = k.today.slice(0, 7);
  const maxMon = months.reduce((m, x) => Math.max(m, x.total), 0);

  const TABS = [["dash", "통합 대시보드"], ["week", "주간 자금소요"], ["mon", "월간 자금소요"]];

  return (
    <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
      <div className="max-w-[1440px] mx-auto p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-teal-600" />
            <h2 className="text-xl font-bold text-slate-800">지급기일 관제</h2>
            <span className="text-xs text-slate-400 ml-1">발주 계획 앱의 구매계획 이력을 읽어 지급기일을 산정합니다 · 조회 전용</span>
          </div>
          <div className="flex items-center gap-2">
            <a href={PROCUREMENT_URL} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
              title="지급 완료 기록·전표 수정은 발주 계획 앱에서 합니다">
              <ExternalLink className="h-4 w-4" />발주 앱 열기
            </a>
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50">
              <RefreshCw className={"h-4 w-4" + (loading ? " animate-spin" : "")} />새로고침
            </button>
            <button onClick={onClose}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
              <X className="h-4 w-4" />닫기
            </button>
          </div>
        </div>

        {/* 탭 + 기준환율 */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={"px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all " +
                (tab === key ? "bg-teal-700 border-teal-700 text-white" : "bg-white border-slate-300 text-slate-500 hover:border-teal-600 hover:text-teal-700")}>
              {label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-[12.5px] text-slate-500 flex-wrap">
            <span className="flex items-baseline gap-1.5 px-2.5 py-1 rounded-md bg-slate-900 text-slate-100"
              title="첫 화면 상단 '오늘의 환율'을 그대로 씁니다. 이 화면에서는 따로 입력하지 않습니다.">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">기준환율 USD</span>
              {fx > 0
                ? <><b className="tabular-nums">{fx.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}</b><span className="text-[10px] text-slate-400">KRW</span></>
                : <span className="text-slate-400">불러오는 중…</span>}
            </span>
            {fx > 0 && fxAt && <span className="text-[11px] text-slate-400">{fxAt} 기준 · 오늘의 환율</span>}
            <select value={fxMode} onChange={(e) => applyFxMode(e.target.value)}
              className="border border-slate-300 rounded-md px-2 py-1 text-[12.5px]">
              <option value="plan">이력 환율 우선 · 없으면 오늘의 환율</option>
              <option value="all">오늘의 환율 일괄 적용</option>
            </select>
          </div>
        </div>

        {err && <p className="text-red-600 text-sm mb-3">불러오기 오류: {err}</p>}
        {plans === null && <p className="text-slate-500 text-sm py-10 text-center">불러오는 중…</p>}

        {plans && !open.length && !err && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500">
            미지급 건이 없습니다. 발주 계획 앱에 발주를 저장하고 결제조건을 입력하면 지급기일이 산정됩니다.
          </div>
        )}

        {plans && open.length > 0 && (
          <>
            {/* ─── 통합 대시보드 ─── */}
            {tab === "dash" && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                  <Card label={`미지급 총액 · ${open.length}건`} value={won(k.totK)} unit="원" />
                  <Card label={`연체 · ${k.ov.length}건`} value={won(k.ovK)} unit="원" danger={k.ov.length > 0} />
                  <Card label={`7일 이내 지급 · ${k.d7.length}건`} value={won(k.d7K)} unit="원" />
                  <Card label={`30일 이내 지급 · ${k.d30.length}건`} value={won(k.d30K)} unit="원" />
                  <Card label={`추정 기일 · ${k.est.length}건`} value={k.estPct} unit={`% · ₩${won(k.estK)}`} />
                </div>

                <p className="text-[11.5px] text-slate-500 mb-4">
                  기준일 {k.today}
                  {fx > 0
                    ? ` · 오늘의 환율 USD/KRW ${fx.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}${fxMode === "all" ? "(일괄)" : "(보조)"}`
                    : " · 환율 불러오는 중 — 외화 건은 집계에서 제외됩니다"}
                  {k.maxOv ? ` · 최장 연체 ${k.maxOv}일` : ""}
                  {k.noRate ? ` · 환율 미입력 ${k.noRate}건 제외` : ""}
                  {k.noShip ? ` · 선적일 미입력 ${k.noShip}건` : ""}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <h3 className="text-sm font-bold text-slate-800">업체별 미지급 잔액</h3>
                    <p className="text-[11.5px] text-slate-400 mb-3">원화환산 비중 · 20% 이상은 공급 편중 관리 대상</p>
                    <Bars items={groupBy(open, (r) => r.vendor)} />
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <h3 className="text-sm font-bold text-slate-800">결제조건 구성</h3>
                    <p className="text-[11.5px] text-slate-400 mb-3">산정 기준별 미지급 금액</p>
                    <Bars items={groupBy(open, (r) => r.baseKind + " 기준")} />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                    <span className="text-[12.5px] font-semibold text-slate-700">미지급 원장 <b className="text-teal-700">{ledger.length}</b>건</span>
                    <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1 text-[12.5px]">
                      <option value="">전체 상태</option>
                      <option value="OVERDUE">연체</option>
                      <option value="DUE">금일 지급</option>
                      <option value="PLANNED">지급 예정</option>
                    </select>
                    <select value={fVendor} onChange={(e) => setFVendor(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1 text-[12.5px] max-w-[220px]">
                      <option value="">전체 업체</option>
                      {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <span className="ml-auto text-[11.5px] text-slate-400">지급 완료 처리는 발주 계획 앱에서 합니다</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px] min-w-[1000px]">
                      <thead>
                        <tr className="bg-white border-b border-slate-200 text-slate-500">
                          <th className="text-left font-medium px-3 py-2">전표번호</th>
                          <th className="text-left font-medium px-3 py-2">지급기일</th>
                          <th className="text-right font-medium px-3 py-2">D-day</th>
                          <th className="text-left font-medium px-3 py-2">상태</th>
                          <th className="text-left font-medium px-3 py-2">업체</th>
                          <th className="text-left font-medium px-3 py-2">지급 구분</th>
                          <th className="text-left font-medium px-3 py-2">산정 근거</th>
                          <th className="text-right font-medium px-3 py-2">지급금액</th>
                          <th className="text-right font-medium px-3 py-2">원화환산</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.map((r) => (
                          <tr key={r.planId + "-" + r.seq} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2"><span className="font-mono text-[11.5px] font-bold text-rose-700">{r.slipNo || "—"}</span></td>
                            <td className="px-3 py-2 tabular-nums">{r.due}{r.adjusted && <span className="ml-1 text-[10px] text-slate-400">보정</span>}</td>
                            <td className={"px-3 py-2 text-right font-semibold tabular-nums " + (r.dday < 0 ? "text-rose-600" : r.dday <= 7 ? "text-amber-600" : "text-slate-700")}>
                              {r.dday > 0 ? "D-" + r.dday : r.dday === 0 ? "D-DAY" : "+" + -r.dday + "일"}
                            </td>
                            <td className="px-3 py-2"><Pill tone={DUE_ST[r.status][1]}>{DUE_ST[r.status][0]}</Pill></td>
                            <td className="px-3 py-2 font-semibold text-slate-800">{r.vendor}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {r.label}
                              <ShipRef r={r} />
                            </td>
                            <td className="px-3 py-2 text-[11.5px] text-slate-400">
                              {r.term}<br />{r.baseKind} {r.baseDate}
                              {r.shipMissing && <><br /><span className="text-amber-600 font-semibold">{r.isRest ? "미선적 잔량 · 납기로 추정" : "선적일 미입력 · 납기로 대용"}</span></>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(r)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">
                              {r.krw == null ? <span className="text-amber-600">환율 미입력</span> : "₩" + won(r.krw)}
                              {r.rate > 0 && <div className="text-[10px] text-slate-400 font-normal">@{r.rate.toLocaleString("ko-KR", { maximumFractionDigits: 2 })} {r.rateSrc === "fx" ? "오늘의 환율" : "이력환율"}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* ─── 주간 자금소요 ─── */}
            {tab === "week" && (
              <PeriodTable
                buckets={weeks}
                curs={curs}
                head="주차별 소요 자금 · 기준일로부터 12주 · 원화환산"
                note="주차 행 아래 세로선 블록이 해당 주차의 건별 내역 · 같은 업체도 회차별로 분리 · 붉은 줄은 연체"
                firstCol="주차"
                labelOf={(b) => b.label}
                subOf={(b) => (b.start ? b.start + " ~ " + b.end : "기일 경과분")}
                capOf={(b) => (b.lead ? "기일 경과분" : b.label + " · " + (b.start ? dueMD(b.start) + "–" + dueMD(b.end) : ""))}
              />
            )}

            {/* ─── 월간 자금소요 ─── */}
            {tab === "mon" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <Card label="6개월 소요 자금" value={won(months.slice(1).reduce((t, m) => t + m.total, 0))} unit="원" />
                  <Card label={`이월(연체) · ${months[0].items.length}건`} value={won(months[0].total)} unit="원" danger={months[0].total > 0} />
                  <Card label="최대 소요 월" value={won(maxMon)} unit="원"
                    sub={(months.slice(1).find((m) => m.total === maxMon) || {}).label} />
                  <Card label="월 평균" value={won(months.slice(1).reduce((t, m) => t + m.total, 0) / 6)} unit="원" />
                </div>
                <PeriodTable
                  buckets={months}
                  curs={curs}
                  head="월별 소요 자금 · 6개월 · 원화환산"
                  note="월 행 아래 세로선 블록이 해당 월의 건별 내역 · 붉은 줄은 연체"
                  firstCol="월"
                  monthly
                  nowKey={cm}
                  labelOf={(b) => b.label}
                  subOf={() => ""}
                  capOf={(b) => (b.lead ? "이월 · 지난달까지 미지급" : b.label)}
                />
                <div className="rounded-2xl border border-slate-200 p-4 mt-4">
                  <h3 className="text-sm font-bold text-slate-800">월별 소요 자금</h3>
                  <p className="text-[11.5px] text-slate-400 mb-3">지급기일 기준 · 원화환산</p>
                  <Bars items={months.slice(1).map((m) => ({ n: m.label, v: m.total }))} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// 주간·월간 공용 표 — 기간 행 + 그 아래 건별 내역 블록
function PeriodTable({ buckets, curs, head, note, firstCol, labelOf, subOf, capOf, monthly, nowKey }) {
  let acc = 0;
  const max = buckets.reduce((m, b) => Math.max(m, b.total), 0);
  const cols = 4 + curs.length + (monthly ? 1 : 0);
  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="text-[12.5px] font-semibold text-slate-700">{head}</span>
        <span className="ml-auto text-[11.5px] text-slate-400">{note}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px] min-w-[900px]">
          <thead>
            <tr className="bg-white border-b border-slate-200 text-slate-500">
              <th className="text-left font-medium px-3 py-2">{firstCol}</th>
              {!monthly && <th className="text-left font-medium px-3 py-2">기간</th>}
              <th className="text-right font-medium px-3 py-2">건수</th>
              {curs.map((c) => <th key={c} className="text-right font-medium px-3 py-2">{c}</th>)}
              <th className="text-right font-medium px-3 py-2">{monthly ? "월 합계" : "주간 합계"}</th>
              {monthly && <th className="text-right font-medium px-3 py-2">전월 대비</th>}
              <th className="text-right font-medium px-3 py-2">누적</th>
              <th className="text-right font-medium px-3 py-2">추정분</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b, i) => {
              acc += b.total;
              const band = b.lead ? "bg-rose-50" : monthly && i % 2 ? "bg-slate-50" : "bg-white";
              let trend = null;
              if (monthly && i >= 2 && !b.lead) {
                const prev = buckets[i - 1].total;
                if (prev <= 0 && b.total > 0) trend = <span className="text-rose-600 font-bold">▲ 신규</span>;
                else if (prev > 0) {
                  const d = ((b.total - prev) / prev) * 100;
                  trend = Math.abs(d) < 1
                    ? <span className="text-slate-400">— 보합</span>
                    : <span className={"font-bold " + (d > 0 ? "text-rose-600" : "text-emerald-600")}>{(d > 0 ? "▲ +" : "▼ ") + Math.round(d)}%</span>;
                }
              }
              return (
                <React.Fragment key={b.label + i}>
                  <tr className={band + (monthly ? " border-t-2 border-slate-300" : " border-b border-slate-100")}>
                    <td className={"px-3 " + (monthly ? "py-3" : "py-2")}>
                      <span className={"inline-flex items-center gap-2 font-bold " + (monthly ? "text-[15px] " : "text-[12.5px] ") + (b.lead ? "text-rose-600" : "text-slate-800")}>
                        {monthly && <i className={"inline-block w-1 h-4 rounded-sm " + (b.lead ? "bg-rose-500" : "bg-teal-700")} />}
                        {labelOf(b)}
                      </span>
                      {monthly && b.key === nowKey && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-700 text-white">이번 달</span>}
                      {monthly && !b.items.length && <span className="ml-2 text-[11.5px] text-slate-400">· 지급 없음</span>}
                    </td>
                    {!monthly && <td className="px-3 py-2 text-[11.5px] text-slate-400">{subOf(b)}</td>}
                    <td className="px-3 py-2 text-right tabular-nums">{b.items.length || "—"}</td>
                    {curs.map((c) => <td key={c} className="px-3 py-2 text-right tabular-nums">{b.byCur[c] ? won(b.byCur[c]) : "—"}</td>)}
                    <td className="px-3 py-2 text-right">
                      <div className={"tabular-nums font-extrabold " + (monthly ? "text-[17px] " : "text-[13px] ") + (b.lead ? "text-rose-600" : "text-slate-900")}>
                        {b.total ? won(b.total) : "—"}
                      </div>
                      {monthly && b.total > 0 && (
                        <div className="h-1.5 rounded bg-slate-200 mt-1.5">
                          <div className={"h-full rounded " + (b.lead ? "bg-rose-500" : "bg-teal-700")} style={{ width: (max > 0 ? (b.total / max) * 100 : 0) + "%" }} />
                        </div>
                      )}
                    </td>
                    {monthly && <td className="px-3 py-2 text-right tabular-nums text-[12.5px]">{trend || <span className="text-slate-400">—</span>}</td>}
                    <td className="px-3 py-2 text-right tabular-nums text-slate-400">{won(acc)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-400">{b.est ? won(b.est) : "—"}</td>
                  </tr>
                  {b.items.length > 0 && (
                    <tr className={band}>
                      <td colSpan={cols} className="pb-3 pr-3">
                        <ItemBlock bucket={b} cap={capOf(b)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
